import { useState, type ReactNode } from "react";

import {
  useDocumentAddGroup,
  useDocumentAddUser,
  useDocumentGroupMembers,
  useDocumentMembers,
  useDocumentRemoveGroup,
  useDocumentRemoveUser,
  useDocumentUsers,
} from "../api/documents";
import { useGroups, useUsersList } from "../api/queries";
import type { Permission } from "../api/types";
import { Button } from "../ui/Button";
import { useToast } from "../ui/toast-context";

const PERMISSIONS: { value: Permission; label: string }[] = [
  { value: "read", label: "Can read" },
  { value: "read_write", label: "Can edit" },
  { value: "admin", label: "Can manage" },
];

/**
 * Direct access to one document (`docs/spec/permissions.md` §4). A grant with a
 * `sourceId` was inherited from a collection or an ancestor page and is shown
 * but not editable here — it is changed where it was made. `documents.users`
 * adds everyone who reaches the document some other way, so the list answers
 * "who can see this?" and not merely "who was named?".
 */
export function DocumentAccessList({
  documentId,
  enabled,
  canManage,
}: {
  documentId: string;
  enabled: boolean;
  canManage: boolean;
}): ReactNode {
  const members = useDocumentMembers(documentId, enabled);
  const groupMembers = useDocumentGroupMembers(documentId, enabled);
  const withAccess = useDocumentUsers(documentId, enabled);
  const users = useUsersList({ limit: 100, offset: 0 }, enabled && canManage);
  const groups = useGroups({});
  const addUser = useDocumentAddUser();
  const removeUser = useDocumentRemoveUser();
  const addGroup = useDocumentAddGroup();
  const removeGroup = useDocumentRemoveGroup();
  const toast = useToast();

  const [userId, setUserId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [permission, setPermission] = useState<Permission>("read_write");

  const report = (error: Error): void => {
    toast.show(error.message, "error");
  };

  const memberships = members.data?.memberships ?? [];
  const grantedUserIds = new Set(memberships.map((membership) => membership.userId));

  const nameOf = (id: string): string =>
    (members.data?.users ?? []).find((user) => user.id === id)?.name ??
    (users.data?.users ?? []).find((user) => user.id === id)?.name ??
    id;

  const groupNameOf = (id: string): string =>
    (groupMembers.data?.groups ?? []).find((group) => group.id === id)?.name ??
    (groups.data?.groups ?? []).find((group) => group.id === id)?.name ??
    id;

  return (
    <section aria-label="People with access" className="mt-5 flex flex-col gap-2">
      <h3 className="text-sm font-medium">People with access</h3>

      <ul
        aria-label="Document members"
        className="divide-y divide-line rounded-md border border-line"
      >
        {memberships.map((membership) => (
          <li key={membership.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <span className="min-w-0 flex-1 truncate">{nameOf(membership.userId)}</span>
            {membership.sourceId === null && canManage ? (
              <>
                <select
                  aria-label={`Permission for ${nameOf(membership.userId)}`}
                  value={membership.permission}
                  className="rounded-md border border-line bg-raised px-2 py-1 text-sm"
                  onChange={(event) => {
                    addUser.mutate(
                      {
                        id: documentId,
                        userId: membership.userId,
                        permission: event.target.value as Permission,
                      },
                      { onError: report },
                    );
                  }}
                >
                  {PERMISSIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  aria-label={`Remove ${nameOf(membership.userId)}`}
                  onClick={() => {
                    removeUser.mutate(
                      { id: documentId, userId: membership.userId },
                      { onError: report },
                    );
                  }}
                >
                  Remove
                </Button>
              </>
            ) : (
              <span className="text-xs text-muted">
                {membership.sourceId === null ? membership.permission : "Inherited"}
              </span>
            )}
          </li>
        ))}
        {memberships.length === 0 && (
          <li className="px-3 py-2 text-sm text-muted">Nobody has been given access directly.</li>
        )}
      </ul>

      <ul
        aria-label="Document groups"
        className="divide-y divide-line rounded-md border border-line"
      >
        {(groupMembers.data?.groupMemberships ?? []).map((membership) => (
          <li key={membership.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <span className="min-w-0 flex-1 truncate">{groupNameOf(membership.groupId)}</span>
            {membership.sourceId === null && canManage ? (
              <Button
                size="sm"
                aria-label={`Remove ${groupNameOf(membership.groupId)}`}
                onClick={() => {
                  removeGroup.mutate(
                    { id: documentId, groupId: membership.groupId },
                    { onError: report },
                  );
                }}
              >
                Remove
              </Button>
            ) : (
              <span className="text-xs text-muted">
                {membership.sourceId === null ? membership.permission : "Inherited"}
              </span>
            )}
          </li>
        ))}
        {(groupMembers.data?.groupMemberships ?? []).length === 0 && (
          <li className="px-3 py-2 text-sm text-muted">No groups have been given access.</li>
        )}
      </ul>

      {canManage && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-sm">
            Person
            <select
              aria-label="Person to add"
              value={userId}
              className="rounded-md border border-line bg-raised px-2 py-1 text-sm"
              onChange={(event) => {
                setUserId(event.target.value);
              }}
            >
              <option value="">Choose…</option>
              {(users.data?.users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Group
            <select
              aria-label="Group to add"
              value={groupId}
              className="rounded-md border border-line bg-raised px-2 py-1 text-sm"
              onChange={(event) => {
                setGroupId(event.target.value);
              }}
            >
              <option value="">Choose…</option>
              {(groups.data?.groups ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Permission
            <select
              aria-label="Permission to grant"
              value={permission}
              className="rounded-md border border-line bg-raised px-2 py-1 text-sm"
              onChange={(event) => {
                setPermission(event.target.value as Permission);
              }}
            >
              {PERMISSIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <Button
            size="sm"
            variant="primary"
            disabled={userId === "" && groupId === ""}
            onClick={() => {
              if (userId !== "") {
                addUser.mutate({ id: documentId, userId, permission }, { onError: report });
                setUserId("");
              }
              if (groupId !== "") {
                addGroup.mutate({ id: documentId, groupId, permission }, { onError: report });
                setGroupId("");
              }
            }}
          >
            Add
          </Button>
        </div>
      )}

      {(withAccess.data ?? []).some((user) => !grantedUserIds.has(user.id)) && (
        <p className="text-xs text-muted">
          Also has access through the collection:{" "}
          {(withAccess.data ?? [])
            .filter((user) => !grantedUserIds.has(user.id))
            .map((user) => user.name)
            .join(", ")}
        </p>
      )}
    </section>
  );
}
